import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
} from '@mui/material';
import {
  Inventory as InventoryIcon,
  LocalShipping as DeliveryIcon,
  Analytics as AnalyticsIcon,
  Campaign as MarketingIcon,
  MobileOffOutlined as MobileIcon,
  Security as SecurityIcon,
  CloudSync as CloudIcon,
  Support as SupportIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const ServicesPage = () => {
  const navigate = useNavigate();

  const services = [
    {
      icon: <InventoryIcon sx={{ fontSize: 50, color: 'primary.main' }} />,
      title: 'Inventory Management',
      description: 'Complete control over your inventory with real-time tracking and automated alerts.',
      features: [
        'Real-time stock tracking',
        'Automated low stock alerts',
        'Barcode scanning support',
        'Multi-location inventory',
        'Product categorization',
        'Bulk import/export tools',
      ],
    },
    {
      icon: <DeliveryIcon sx={{ fontSize: 50, color: 'primary.main' }} />,
      title: 'Delivery Management',
      description: 'Track deliveries from warehouse to customer door with GPS tracking and automated notifications.',
      features: [
        'Real-time GPS tracking',
        'Delivery route optimization',
        'Customer notifications',
        'Proof of delivery',
        'Delivery scheduling',
        'Driver mobile app',
      ],
    },
    {
      icon: <MarketingIcon sx={{ fontSize: 50, color: 'primary.main' }} />,
      title: 'CRM & Marketing',
      description: 'Comprehensive customer relationship management with integrated marketing automation tools.',
      features: [
        'Lead management',
        'Sales pipeline tracking',
        'Email campaigns',
        'Customer segmentation',
        'Social media automation',
        'Marketing analytics',
      ],
    },
    {
      icon: <AnalyticsIcon sx={{ fontSize: 50, color: 'primary.main' }} />,
      title: 'Business Analytics',
      description: 'Make data-driven decisions with comprehensive reporting and business intelligence tools.',
      features: [
        'Sales performance reports',
        'Inventory turnover analysis',
        'Customer behavior insights',
        'Profit margin tracking',
        'Custom dashboards',
        'Automated reporting',
      ],
    },
    {
      icon: <MobileIcon sx={{ fontSize: 50, color: 'primary.main' }} />,
      title: 'Mobile Access',
      description: 'Access your business data anywhere with our mobile-optimized interface and dedicated apps.',
      features: [
        'Responsive web interface',
        'Mobile inventory scanning',
        'Offline capability',
        'Push notifications',
        'Mobile order management',
        'Field sales tools',
      ],
    },
    {
      icon: <CloudIcon sx={{ fontSize: 50, color: 'primary.main' }} />,
      title: 'Cloud Integration',
      description: 'Seamless integration with popular business tools and cloud-based data synchronization.',
      features: [
        'Multi-location sync',
        'Third-party integrations',
        'API access',
        'Data backup & recovery',
        'Scalable infrastructure',
        'Real-time updates',
      ],
    },
  ];

  const additionalServices = [
    {
      icon: <SecurityIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'Enterprise Security',
      description: 'Bank-level security with encrypted data transmission and secure user authentication.',
    },
    {
      icon: <SupportIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: '24/7 Customer Support',
      description: 'Round-the-clock support with dedicated account managers for enterprise clients.',
    },
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
          color: 'white',
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h2"
            component="h1"
            gutterBottom
            sx={{
              fontWeight: 'bold',
              fontSize: { xs: '2.5rem', md: '3.5rem' },
            }}
          >
            Our Services
          </Typography>
          <Typography
            variant="h5"
            sx={{
              mb: 4,
              opacity: 0.9,
              fontSize: { xs: '1.25rem', md: '1.5rem' },
            }}
          >
            Comprehensive inventory management solutions designed for modern businesses
          </Typography>
        </Container>
      </Box>

      {/* Main Services */}
      <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: 'grey.50' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary' }}>
              Complete Business Solutions
            </Typography>
            <Typography variant="h6" sx={{ color: 'text.secondary', maxWidth: 600, mx: 'auto' }}>
              Everything you need to manage and grow your business, all in one integrated platform
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {services.map((service, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    p: 3,
                    transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: 8,
                    },
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      {service.icon}
                      <Typography variant="h5" component="h3" sx={{ ml: 2, fontWeight: 'bold' }}>
                        {service.title}
                      </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                      {service.description}
                    </Typography>
                    <List dense>
                      {service.features.map((feature, featureIndex) => (
                        <ListItem key={featureIndex} sx={{ px: 0 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <CheckIcon color="primary" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText 
                            primary={feature} 
                            primaryTypographyProps={{ fontSize: '0.9rem' }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Additional Services */}
      <Box sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
              Enterprise-Grade Features
            </Typography>
            <Typography variant="h6" sx={{ color: 'text.secondary' }}>
              Built for scale with enterprise security and support
            </Typography>
          </Box>

          <Grid container spacing={4} justifyContent="center">
            {additionalServices.map((service, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card
                  sx={{
                    p: 4,
                    textAlign: 'center',
                    height: '100%',
                    transition: 'transform 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                    },
                  }}
                >
                  <CardContent>
                    <Box sx={{ mb: 3 }}>
                      {service.icon}
                    </Box>
                    <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
                      {service.title}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {service.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Process Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: 'grey.50' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
              How It Works
            </Typography>
            <Typography variant="h6" sx={{ color: 'text.secondary' }}>
              Get started in just a few simple steps
            </Typography>
          </Box>

          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    backgroundColor: 'primary.main',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '2rem',
                    fontWeight: 'bold',
                    mx: 'auto',
                    mb: 3,
                  }}
                >
                  1
                </Box>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Sign Up & Setup
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Create your account and set up your store locations. We'll help you import your existing inventory data.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    backgroundColor: 'primary.main',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '2rem',
                    fontWeight: 'bold',
                    mx: 'auto',
                    mb: 3,
                  }}
                >
                  2
                </Box>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Configure & Customize
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Customize the system to match your business processes. Set up users, locations, and configure your preferences.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    backgroundColor: 'primary.main',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '2rem',
                    fontWeight: 'bold',
                    mx: 'auto',
                    mb: 3,
                  }}
                >
                  3
                </Box>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Go Live & Grow
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Start managing your inventory and deliveries. Use our analytics to optimize your operations and grow your business.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          backgroundColor: 'primary.main',
          color: 'white',
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
            Ready to Transform Your Business?
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
            Start your free 14-day trial today and see how DymenStock can help streamline your operations.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexDirection: { xs: 'column', sm: 'row' } }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/login')}
              sx={{
                backgroundColor: 'white',
                color: 'primary.main',
                px: 4,
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                '&:hover': {
                  backgroundColor: 'grey.100',
                },
              }}
            >
              Start Free Trial
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/contact')}
              sx={{
                borderColor: 'white',
                color: 'white',
                px: 4,
                py: 2,
                fontSize: '1.1rem',
                '&:hover': {
                  borderColor: 'grey.300',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              Schedule Demo
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default ServicesPage;
